import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import { StudioCollection } from './GuidePages';
import { CountryMap } from '../components/studio/CountryMap';
import { FoundersCollections } from '../components/studio/FoundersCollections';
import { ProductCard } from '../components/shop/ProductCard';
import { PaymentModal } from '../components/shop/PaymentModal';
import { ComingSoon } from '../components/shop/ComingSoon';
import { RelocationByCountry } from '../components/shop/RelocationByCountry';
import { usePageSeo } from '../lib/seo';
import { DestinationAlertForm } from '../components/studio/TownAlertForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RELOCATION = 'Relocation Expat Guides';
const FAMILY = 'Relocation Guides for Families';
const STANDALONE = 'Standalone Guides';
const SECTIONS = [
  { id: 'relocation-expat-guides', label: RELOCATION },
  { id: 'family-relocation-guides', label: FAMILY },
  { id: 'founders-collections', label: "Founder's Collections" },
  { id: 'small-towns-series', label: 'Small Towns Series' },
  { id: 'standalone-guides', label: STANDALONE },
];

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

const SectionHeading = ({ eyebrow, title, intro }) => (
  <div className="mb-10">
    <span className="eyebrow mb-3"><span className="w-10 h-px bg-[#C5A059]" /> {eyebrow}</span>
    <h2 className="font-display text-4xl md:text-5xl font-medium text-[#1E1622] mb-3">{title}</h2>
    <p className="text-[#1E1622]/60 max-w-2xl font-light">{intro}</p>
  </div>
);

const ProductSection = ({ id, title, eyebrow, intro, products, loading, onBuy, footer, body }) => (
  <section id={id} data-testid={`shop-section-${id}`} className="container-custom scroll-mt-28 mt-24">
    <SectionHeading eyebrow={eyebrow} title={title} intro={intro} />
    {loading ? (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">{[0, 1, 2].map((i) => <div key={i} className="bg-[#F5EFE9] rounded-2xl h-96 animate-pulse" />)}</div>
    ) : products.length === 0 ? (
      <ComingSoon section={title} />
    ) : body || (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.08 }}>
            <ProductCard product={p} onBuy={onBuy} />
          </motion.div>
        ))}
      </div>
    )}
    {footer}
  </section>
);

const ShopPage = () => {
  usePageSeo('shop', { title: 'Shop Relocation Guides for Moving to Europe', description: 'Complete relocation guides for Australians moving to Italy, Portugal, Spain and Canada, plus the Small Towns Series town guides and regional packs.' });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const { hash } = useLocation();

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data.products || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 900);
    return () => clearTimeout(t);
  }, [hash]);

  const isFamily = (p) => p.family || p.edition_type === 'family';
  const relocation = products.filter((p) => p.source === 'guide_studio' && !isFamily(p));
  const familyGuides = products.filter((p) => p.source === 'guide_studio' && isFamily(p));
  const standalone = products.filter((p) => p.source !== 'guide_studio' && !['Small Towns of Italy', 'Small Towns Series'].includes(p.category));
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="min-h-screen pt-24 pb-20">
      <section data-testid="shop-header" className="container-custom mb-10">
        <motion.div {...fadeUp} className="text-center">
          <span className="eyebrow justify-center mb-5"><span className="w-10 h-px bg-[#C5A059]" /> The Collection</span>
          <h1 className="font-display text-5xl md:text-6xl font-medium text-[#1E1622] mb-4">Relocation Kits &amp; Guides</h1>
          <p className="text-[#1E1622]/60 max-w-2xl mx-auto font-light text-lg">Beautifully designed resources to guide you through every step of your international move.</p>
        </motion.div>
      </section>

      <nav data-testid="shop-section-nav" className="container-custom flex flex-wrap justify-center gap-3">
        {SECTIONS.map((s, i) => (
          <button key={s.id} onClick={() => jump(s.id)} data-testid={`shop-jump-${s.id}`}
            className="group inline-flex items-center gap-3 rounded-full border border-[#E6DAC5] bg-white px-6 py-3 text-sm text-[#1E1622] transition-colors duration-200 hover:border-[#C5A059] hover:text-[#3B164C]">
            <span className="font-display text-lg text-[#C5A059]">{String(i + 1).padStart(2, '0')}</span> {s.label}
            <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
        ))}
      </nav>

      <ProductSection id="relocation-expat-guides" title={RELOCATION} eyebrow="Standard editions" loading={loading} onBuy={setBuying}
        intro="Everything you need to plan your own move, from visas and banking to housing and settling in. Choose your destination to see the complete guide, the money-saving bundles and each individual volume." products={relocation}
        body={<RelocationByCountry products={relocation} onBuy={setBuying} />} />

      <ProductSection id="family-relocation-guides" title={FAMILY} eyebrow="Family editions" loading={loading} onBuy={setBuying}
        intro="Written for parents moving with children, with extra guidance on family visas, children's rights, school enrolment, family housing, healthcare and long-term planning for your family's future." products={familyGuides}
        body={<RelocationByCountry products={familyGuides} onBuy={setBuying} family />}
        footer={<div className="max-w-3xl mx-auto mt-12"><DestinationAlertForm /></div>} />

      <FoundersCollections />

      <span id="small-towns-of-italy" className="block scroll-mt-28" aria-hidden="true" />
      <section id="small-towns-series" data-testid="shop-section-small-towns-series" className="container-custom scroll-mt-28">
        <StudioCollection map={(
          <div className="mb-12 rounded-2xl overflow-hidden border border-[#C5A059]/30" data-testid="shop-town-map">
            <p className="bg-[#2D1037] text-[#FAF7F2]/80 text-sm px-5 py-3">Tap a pin to open that town's guide.</p>
            <CountryMap testPrefix="shop-town-map" dark />
          </div>
        )} />
      </section>

      <ProductSection id="standalone-guides" title={STANDALONE} eyebrow="Specialist guides" loading={loading} onBuy={setBuying}
        intro="Focused guides for specific parts of your move, such as relocating with pets." products={standalone} />

      <section data-testid="shop-cta" className="container-custom mt-24">
        <motion.div {...fadeUp} className="bg-[#F5EFE9] rounded-3xl p-8 md:p-12 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-[#1E1622] mb-4">Not sure where to start?</h2>
          <p className="text-[#1E1622]/70 max-w-xl mx-auto mb-6">Get our free guide "10 Things Nobody Tells You About Moving to Italy" and discover what to expect before you make the big move.</p>
          <Link to="/freebie" data-testid="shop-free-guide-cta" className="btn-primary inline-flex items-center gap-2">Get Free Guide <ArrowRight size={18} /></Link>
        </motion.div>
      </section>

      <PaymentModal product={buying} onClose={() => setBuying(null)} />
    </div>
  );
};

export default ShopPage;
